import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { feedbackAPI } from '../lib/api';
import { useAuthStore } from '../store';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import {
  MessageSquarePlus, Loader2, Send, EyeOff, User, Filter, Inbox, CornerDownRight,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = {
  new: 'bg-blue-100 text-blue-700',
  read: 'bg-slate-100 text-slate-700',
  noted: 'bg-amber-100 text-amber-700',
  applied: 'bg-emerald-100 text-emerald-700',
  dismissed: 'bg-red-100 text-red-700',
};
const STATUS_ORDER = ['new', 'read', 'noted', 'applied', 'dismissed'];

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

export default function FeedbackPage() {
  const user = useAuthStore((s) => s.user);
  const isCeo = user?.role === 'ceo' || user?.role === 'manager';

  const [meta, setMeta] = useState({ categories: {}, statuses: {} });
  const [loading, setLoading] = useState(true);

  // member
  const [form, setForm] = useState({ category: 'procesos', text: '', anonymous: false });
  const [sending, setSending] = useState(false);
  const [mine, setMine] = useState([]);
  const [generalStats, setGeneralStats] = useState(null);

  // ceo
  const [all, setAll] = useState([]);
  const [fCategory, setFCategory] = useState('__all');
  const [fStatus, setFStatus] = useState('__all');
  const [replyDrafts, setReplyDrafts] = useState({});
  const [replying, setReplying] = useState(null);

  const loadMeta = useCallback(async () => {
    try { setMeta(await feedbackAPI.getMeta()); } catch (e) { /* noop */ }
  }, []);

  const loadMember = useCallback(async () => {
    try {
      const d = await feedbackAPI.getMine();
      setMine(d.mine || []);
      setGeneralStats(d.general_stats || null);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  const loadCeo = useCallback(async () => {
    setLoading(true);
    try {
      const d = await feedbackAPI.getAll({ category: fCategory, status: fStatus });
      setAll(d.feedback || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [fCategory, fStatus]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { if (isCeo) loadCeo(); else loadMember(); }, [isCeo, loadCeo, loadMember]);

  const submit = async () => {
    if (!form.text.trim()) { toast.error('Escribe tu feedback'); return; }
    setSending(true);
    try {
      await feedbackAPI.submit(form);
      toast.success(form.anonymous ? 'Feedback anónimo enviado. ¡Gracias!' : 'Feedback enviado. ¡Gracias!');
      setForm({ category: 'procesos', text: '', anonymous: false });
      loadMember();
    } catch (e) { toast.error(e.message || 'No se pudo enviar'); }
    setSending(false);
  };

  const changeStatus = async (id, status) => {
    try {
      await feedbackAPI.setStatus(id, status);
      setAll((prev) => prev.map((f) => (f.id === id ? { ...f, status, status_label: meta.statuses[status] } : f)));
      // si hay filtro de estado activo, recargar para que salga/entre de la lista
      if (fStatus !== '__all') loadCeo();
      toast.success('Estado actualizado');
    } catch (e) { toast.error(e.message); }
  };

  const sendReply = async (id) => {
    const text = (replyDrafts[id] || '').trim();
    if (!text) { toast.error('Escribe una respuesta'); return; }
    setReplying(id);
    try {
      const r = await feedbackAPI.reply(id, text);
      setAll((prev) => prev.map((f) => (f.id === id ? { ...f, reply: r.reply, reply_by: r.reply_by, reply_at: r.reply_at } : f)));
      setReplyDrafts((prev) => ({ ...prev, [id]: '' }));
      toast.success('Respuesta enviada a la autora');
    } catch (e) { toast.error(e.message || 'No se pudo enviar'); }
    setReplying(null);
  };

  const cats = meta.categories || {};
  const stats = meta.statuses || {};

  if (loading && !isCeo && mine.length === 0 && !generalStats) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  // ============ VISTA CEO / MANAGER ============
  if (isCeo) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6" data-testid="feedback-page">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2"><Inbox className="w-7 h-7 text-primary" /> Feedback del equipo</h1>
          <p className="text-muted-foreground mt-1">Mejoras y sugerencias que nos manda el equipo. Cambia el estado para que sepan que las tenéis en cuenta.</p>
        </div>

        {/* Filtros */}
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm text-muted-foreground flex items-center gap-1.5"><Filter className="w-4 h-4" /> Filtrar</span>
          <select value={fCategory} onChange={(e) => setFCategory(e.target.value)} className="border rounded-lg px-3 py-1.5 text-sm bg-background" data-testid="feedback-filter-category">
            <option value="__all">Todas las categorías</option>
            {Object.entries(cats).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} className="border rounded-lg px-3 py-1.5 text-sm bg-background" data-testid="feedback-filter-status">
            <option value="__all">Todos los estados</option>
            {Object.entries(stats).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <Badge variant="outline" className="ml-auto">{all.length} feedback{all.length === 1 ? '' : 's'}</Badge>
        </div>

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : all.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-muted-foreground">Aún no hay feedback con estos filtros.</CardContent></Card>
        ) : (
          <div className="space-y-3">
            {all.map((f) => (
              <Card key={f.id} data-testid={`feedback-item-${f.id}`}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="secondary">{f.category_label}</Badge>
                    {f.anonymous ? (
                      <Badge variant="outline" className="text-muted-foreground"><EyeOff className="w-3 h-3 mr-1" /> Anónimo</Badge>
                    ) : (
                      <Badge variant="outline"><User className="w-3 h-3 mr-1" /> {f.author_name || 'Firmado'}</Badge>
                    )}
                    <span className="text-xs text-muted-foreground">{fmtDate(f.created_at)}</span>
                    <Badge className={`ml-auto ${STATUS_COLORS[f.status]}`} data-testid={`feedback-status-badge-${f.id}`}>{f.status_label}</Badge>
                  </div>
                  <p className="text-sm whitespace-pre-wrap leading-relaxed">{f.text}</p>
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {STATUS_ORDER.map((s) => (
                      <button
                        key={s}
                        onClick={() => changeStatus(f.id, s)}
                        className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${f.status === s ? STATUS_COLORS[s] + ' border-transparent font-medium' : 'text-muted-foreground hover:bg-muted'}`}
                        data-testid={`feedback-set-${s}-${f.id}`}
                      >
                        {stats[s]}
                      </button>
                    ))}
                  </div>

                  {/* Respuesta (solo firmados: hay a quién avisar) */}
                  {f.reply && (
                    <div className="flex gap-2 text-sm bg-muted/40 rounded-lg p-3" data-testid={`feedback-reply-${f.id}`}>
                      <CornerDownRight className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                      <div>
                        <span className="font-medium">{f.reply_by || 'CEO'}: </span>
                        <span className="whitespace-pre-wrap">{f.reply}</span>
                      </div>
                    </div>
                  )}
                  {!f.anonymous && (
                    <div className="flex items-start gap-2 pt-1">
                      <textarea
                        value={replyDrafts[f.id] || ''}
                        onChange={(e) => setReplyDrafts((prev) => ({ ...prev, [f.id]: e.target.value }))}
                        rows={1}
                        placeholder={f.reply ? 'Editar / añadir respuesta...' : 'Responde a la autora para cerrar el círculo...'}
                        className="flex-1 border rounded-lg px-3 py-2 text-sm bg-background resize-y min-h-[38px]"
                        data-testid={`feedback-reply-input-${f.id}`}
                      />
                      <Button size="sm" disabled={replying === f.id} onClick={() => sendReply(f.id)} data-testid={`feedback-reply-btn-${f.id}`}>
                        {replying === f.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </motion.div>
    );
  }

  // ============ VISTA MIEMBRO ============
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6" data-testid="feedback-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2"><MessageSquarePlus className="w-7 h-7 text-primary" /> Feedback</h1>
        <p className="text-muted-foreground mt-1">Un canal para proponer mejoras a Karmele y Marta. Puedes firmarlo o enviarlo de forma anónima.</p>
      </div>

      <Card data-testid="feedback-form-card">
        <CardHeader>
          <CardTitle className="text-lg">Enviar feedback</CardTitle>
          <CardDescription>Cuéntanos qué se puede mejorar. Si lo marcas anónimo, nadie sabrá que fuiste tú.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label>Categoría</Label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm bg-background" data-testid="feedback-category-select">
              {Object.entries(cats).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label>Tu feedback</Label>
            <textarea
              value={form.text}
              onChange={(e) => setForm({ ...form, text: e.target.value })}
              rows={5}
              placeholder="Escribe aquí lo que crees que se puede mejorar, una idea, algo que te preocupa..."
              className="w-full border rounded-lg px-3 py-2 text-sm bg-background resize-y"
              data-testid="feedback-text-input"
            />
          </div>
          <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/40">
            <Checkbox id="anon" checked={form.anonymous} onCheckedChange={(v) => setForm({ ...form, anonymous: !!v })} data-testid="feedback-anonymous-checkbox" />
            <Label htmlFor="anon" className="cursor-pointer flex items-center gap-1.5 text-sm font-normal">
              <EyeOff className="w-4 h-4 text-muted-foreground" /> Enviar de forma anónima (no se guardará quién lo escribe)
            </Label>
          </div>
          <Button onClick={submit} disabled={sending} data-testid="feedback-submit-btn">
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Enviar feedback
          </Button>
        </CardContent>
      </Card>

      {/* Mis feedbacks firmados + estado */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Mis feedbacks enviados</CardTitle>
          <CardDescription>Solo aparecen los que enviaste firmados, con su estado. Los anónimos no se asocian a ti.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {mine.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">Todavía no has enviado feedback firmado.</p>}
          {mine.map((f) => (
            <div key={f.id} className="p-3 rounded-lg border space-y-1.5" data-testid={`my-feedback-${f.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary">{f.category_label}</Badge>
                <span className="text-xs text-muted-foreground">{fmtDate(f.created_at)}</span>
                <Badge className={`ml-auto ${STATUS_COLORS[f.status]}`} data-testid={`my-feedback-status-${f.id}`}>{f.status_label}</Badge>
              </div>
              <p className="text-sm whitespace-pre-wrap">{f.text}</p>
              {f.reply && (
                <div className="flex gap-2 text-sm bg-primary/5 rounded-lg p-2.5 mt-1" data-testid={`my-feedback-reply-${f.id}`}>
                  <CornerDownRight className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                  <div>
                    <span className="font-medium">{f.reply_by || 'CEO'}: </span>
                    <span className="whitespace-pre-wrap">{f.reply}</span>
                  </div>
                </div>
              )}
            </div>
          ))}
          {generalStats && generalStats.total > 0 && (
            <p className="text-xs text-muted-foreground pt-2 border-t">
              En total el equipo ha enviado <strong>{generalStats.total}</strong> feedback{generalStats.total === 1 ? '' : 's'}: {generalStats.by_status.applied} aplicado(s), {generalStats.by_status.noted} en cuenta, {generalStats.by_status.new + generalStats.by_status.read} sin resolver.
            </p>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}
